<template>
  <div>
    <!-- Mobile menu overlay -->
    <!-- Используем v-show для overlay - элемент часто переключается, но должен оставаться в DOM для transition -->
    <transition name="fade">
      <div 
        v-show="isOpen" 
        class="mobile-overlay" 
        @click="$emit('close')"
        aria-hidden="true"
      ></div>
    </transition>
    
    <!-- Mobile menu -->
    <!-- Используем v-show для меню - элемент часто переключается, но должен оставаться в DOM для transition -->
    <transition name="slide">
      <nav 
        v-show="isOpen" 
        id="mobile-menu"
        class="mobile-menu"
        role="navigation"
        aria-label="Главное меню"
      >
                <div class="mobile-menu-content">
          <!-- Верх: Регион, Каталог, Вход в аккаунт -->
          <!-- Регион -->
          <div v-if="authInfo.viewer === anonymousViewer" class="mobile-menu-section">
            <label class="mobile-menu-label" for="region-select">Регион</label>
            <div class="relative">
              <select 
                id="region-select"
                v-model="currentSelectedRegion" 
                class="mobile-menu-select"
                aria-label="Выбрать регион"
              >
                <option v-for="region in regions" :key="region.id" :value="region.id">
                  {{ formatRegionName(region.name) }}
                </option>
              </select>
              <PhCaretDown :size="16" :weight="'regular'" class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500" />
            </div>
          </div>

          <!-- Каталог -->
          <router-link 
            @click="$emit('close')" 
            to="/catalog/" 
            class="mobile-menu-btn mobile-menu-btn-primary"
            aria-label="Перейти в каталог продукции"
          >
            <PhList :size="18" :weight="'fill'" color="white" />
            <span>Каталог</span>
          </router-link>
          
          <!-- Вход в аккаунт / Профиль -->
          <MobileUser @closeMenu="$emit('close')" />
          
          <!-- Разделитель -->
          <div class="mobile-menu-divider" role="separator"></div>
          
          <!-- Навигация по сайту -->
          <div class="mobile-menu-section" role="list">
            <router-link @click="$emit('close')" to="/" class="mobile-menu-link" aria-label="Перейти на главную страницу">
              <PhHouse :size="16" :weight="'regular'" />
              <span>Главная</span>
            </router-link>
            <router-link @click="$emit('close')" to="/about" class="mobile-menu-link" aria-label="Узнать больше о компании КРОН">
              <PhBuildings :size="16" :weight="'regular'" />
              <span>О компании</span>
            </router-link>
            <router-link @click="$emit('close')" to="/stroitelstvo" class="mobile-menu-link" aria-label="Строительные услуги КРОН">
              <PhBuildings :size="16" :weight="'regular'" />
              <span>Строительство</span>
            </router-link>
            <router-link @click="$emit('close')" to="/contacts" class="mobile-menu-link" aria-label="Контакты">
              <PhPhone :size="16" :weight="'regular'" />
              <span>Контакты</span>
            </router-link>
            <router-link @click="$emit('close')" to="/delivery" class="mobile-menu-link" aria-label="Условия доставки">
              <PhTruck :size="16" :weight="'regular'" />
              <span>Доставка</span>
            </router-link>
          </div>
        </div>
      </nav>
    </transition>
  </div>
</template>

<script>
import MobileUser from './MobileUser.vue'
import { PhList, PhCaretDown, PhHouse, PhBuildings, PhPhone, PhTruck } from '@phosphor-icons/vue'
import { useAuth } from '@/store/useAuth'
import { ANONYMOUS_VIEWER } from '@/constants'

export default {
  name: 'MobileMenu',
  components: {
    MobileUser,
    PhList,
    PhCaretDown,
    PhHouse,
    PhBuildings,
    PhPhone,
    PhTruck
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    regions: {
      type: Array,
      default: () => []
    },
    selectedRegion: {
      type: String,
      default: null
    }
  },
  emits: ['close', 'region-change'],
  setup() {
    const authInfo = useAuth()
    return { authInfo }
  },
  computed: {
    // Константа для использования в template
    anonymousViewer() {
      return ANONYMOUS_VIEWER
    },
    currentSelectedRegion: {
      get() {
        // Если selectedRegion передан как prop, используем его
        if (this.selectedRegion) {
          return this.selectedRegion
        }
        // Иначе используем регион из authInfo
        return this.authInfo.region?.id || null
      },
      set(value) {
        this.$emit('region-change', value)
      }
    }
  },
  methods: {
    formatRegionName(name) {
      if (!name) return name;
      // Обрабатываем разные варианты названия (без учета регистра и пробелов)
      const normalizedName = name.trim().toLowerCase().replace(/\s+/g, ' ');
      if (normalizedName === 'московская область' || normalizedName.includes('московская область')) {
        return 'Москва и МО';
      }
      // Дополнительная проверка на возможные варианты
      if (normalizedName.includes('московск') && normalizedName.includes('област')) {
        return 'Москва и МО';
      }
      return name;
    }
  }
}
</script>

<style scoped>
/* ===== MOBILE MENU - CUSTOM ===== */

/* Overlay - белый непрозрачный фон */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: var(--z-mobile-overlay);
}

/* Mobile menu - выезжающее меню сверху из хедера */
.mobile-menu {
  position: fixed;
  top: var(--mobile-header-height, 3.75rem);
  left: 0;
  right: 0;
  width: 100%;
  max-height: calc(100vh - var(--mobile-header-height, 3.75rem));
  max-height: calc(100dvh - var(--mobile-header-height, 3.75rem));
  background: white;
  z-index: var(--z-mobile-menu);
  overflow-y: auto;
  overflow-x: hidden;
  border-bottom: 1px solid var(--slate-200);
  box-shadow: var(--shadow-lg);
  -webkit-overflow-scrolling: touch;
}

.mobile-menu-content {
  padding: 0.75rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

/* Стили кнопок вынесены в app.css для DRY - см. .mobile-menu-btn */

/* Разделитель */
.mobile-menu-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--slate-200), transparent);
  margin: 0.25rem 0;
  flex-shrink: 0;
}

.mobile-menu-section {
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.mobile-menu-select {
  width: 100%;
  min-height: 2.5rem;
  padding: 0.5rem 2.25rem 0.5rem 0.875rem;
  border-radius: 0.625rem;
  border: 1px solid var(--border-soft);
  background: var(--bg-input);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  transition: var(--transition-base);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.mobile-menu-select:focus-visible {
  outline: none;
  border-color: var(--slate-300); /* Серая обводка при фокусе, не красная */
  box-shadow: 0 0 0 3px var(--shadow-light);
}

.mobile-menu-select:focus-visible + .absolute {
  color: var(--slate-600);
}

/* Hover только для устройств с мышью */
@media (hover: hover) {
  .mobile-menu-select:hover + .absolute {
    color: var(--slate-600);
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateY(-100%);
}

/* Hide on desktop */
@media (min-width: 1024px) {
  .mobile-overlay,
  .mobile-menu {
    display: none;
  }
}
</style>

